import { useState } from "react";

export interface ShareHandlers {
  handleWhatsAppShare: () => void;
  handleCopyLink: () => Promise<void>;
  handleSnackbarClose: () => void;
  snackbar: {
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  };
}

interface UseDetailShareProps {
  itemId: string;
  itemType: "event" | "store" | "offer";
  shareText: string;
  incrementShareMutation: (id: string) => void;
}

/**
 * Custom hook for handling share functionality in detail screens
 * - WhatsApp sharing with localStorage tracking to prevent count inflation
 * - Copy to clipboard functionality
 * - Snackbar notifications
 */
export const useDetailShare = ({
  itemId,
  itemType,
  shareText,
  incrementShareMutation,
}: UseDetailShareProps): ShareHandlers => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });

  const handleWhatsAppShare = () => {
    const shareUrl = window.location.href;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
      shareText + shareUrl
    )}`;

    // Check if user has already shared this item (localStorage tracking)
    // Only increment backend counter once per user per item
    const shareKey = `${itemType}_shared_${itemId}`;
    const hasShared = localStorage.getItem(shareKey);

    if (!hasShared) {
      // First time sharing - increment count and mark as shared
      incrementShareMutation(itemId);
      localStorage.setItem(shareKey, "true");
    }

    // Always allow WhatsApp share (good UX)
    window.open(whatsappUrl, "_blank");
    setSnackbar({
      open: true,
      message: "Opening WhatsApp...",
      severity: "success",
    });
  };

  const handleCopyLink = async () => {
    const url = window.location.href;

    try {
      // Modern Clipboard API (works on HTTPS and modern browsers)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers or mobile devices
        const textArea = document.createElement("textarea");
        textArea.value = url;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (!successful) {
          throw new Error("Copy command failed");
        }
      }

      setSnackbar({
        open: true,
        message: "Link copied to clipboard!",
        severity: "success",
      });

      // Also track copy as a share
      const shareKey = `${itemType}_shared_${itemId}`;
      const hasShared = localStorage.getItem(shareKey);

      if (!hasShared) {
        incrementShareMutation(itemId);
        localStorage.setItem(shareKey, "true");
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to copy link",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return {
    handleWhatsAppShare,
    handleCopyLink,
    handleSnackbarClose,
    snackbar,
  };
};
